// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useRef } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import usePopup from "@/hooks/usePopup";

// --- Logic --------------------------------------------------------------------------------------

/**
 * Keeps the history stack one entry deep per open popup so the hardware/browser back button dismisses
 * the top popup instead of navigating away — a route change behind an open popup throws away whatever
 * the user was in the middle of. Every open pushes a placeholder entry, every close consumes one, so
 * the stack lands back exactly where it started once the last popup is gone.
 */
export default function usePopupBackGuard() {
    const depth = usePopup((state) => state.comps.length);
    const depthRef = useRef(0);

    /** Back presses already consumed by the browser — their entries are gone, so we must not pop again. */
    const consumedRef = useRef(0);

    /** Entries we are removing ourselves; the `popstate` they fire is ours and must be ignored. */
    const syntheticRef = useRef(0);

    useEffect(() => {
        const handlePopState = () => {
            if (syntheticRef.current > 0) {
                syntheticRef.current -= 1;
                return;
            }

            const { comps, closing, requestClose } = usePopup.getState();
            const top = [...comps].reverse().find((comp) => !closing.includes(comp.name));
            if (!top) return;

            consumedRef.current += 1;
            requestClose(top.name);
        };

        /**
         * Escape is the keyboard's version of the same gesture, so it lives beside it rather than in
         * each popup. A popup that refuses its own backdrop (`coverClose: false`) is saying it must
         * be answered rather than dismissed, so it refuses this too.
         */
        const handleKey = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return;

            const { comps, closing, requestClose } = usePopup.getState();
            const top = [...comps].reverse().find((comp) => !closing.includes(comp.name));
            if (!top || top.coverClose === false) return;

            requestClose(top.name);
        };

        window.addEventListener("popstate", handlePopState);
        window.addEventListener("keydown", handleKey);
        return () => {
            window.removeEventListener("popstate", handlePopState);
            window.removeEventListener("keydown", handleKey);
        };
    }, []);

    useEffect(() => {
        if (depth > depthRef.current) {
            for (let i = depthRef.current; i < depth; i++) window.history.pushState({ popupDepth: i + 1 }, "");
        } else if (depth < depthRef.current) {
            const closed = depthRef.current - depth;
            const consumed = Math.min(closed, consumedRef.current);
            consumedRef.current -= consumed;

            // Anything closed by its own button or the backdrop still owns a history entry — drop it,
            // but only while our placeholder is still the entry on top. A link inside a popup (the
            // "More" sheet is all links) navigates before the close animation finishes, and popping
            // then would rewind the navigation the user just asked for.
            const orphaned = closed - consumed;
            if (orphaned > 0 && (window.history.state as { popupDepth?: number } | null)?.popupDepth) {
                // A single `go()` traversal fires exactly one `popstate`, however many entries it skips.
                syntheticRef.current += 1;
                window.history.go(-orphaned);
            }
        }

        depthRef.current = depth;
    }, [depth]);
}
